<script>
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();
  let closing = false;

  async function dismiss() {
    closing = true;
    try {
      await fetch('/api/users/combined-ui-notification-seen', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Failed to mark combined UI notification as seen:', err);
    }
    setTimeout(() => dispatch('dismiss'), 200);
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) dismiss();
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') dismiss();
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions a11y-no-noninteractive-element-interactions -->
<!-- Modal Overlay -->
<div
  class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
  class:fade-out={closing}
  on:click={handleOverlayClick}
  role="dialog"
  aria-modal="true"
  aria-label="New layout announcement"
>
  <!-- Modal Content -->
  <div class="bg-white w-full max-w-3xl rounded-lg shadow-lg relative animate-fade-in max-h-[90vh] overflow-y-auto">

    <!-- Close Button -->
    <button
      on:click={dismiss}
      class="absolute top-4 right-4 rounded opacity-70 hover:opacity-100 transition-opacity z-10 p-1 hover:bg-gray-100 border-0"
      aria-label="Close"
    >
      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>

    <!-- Header -->
    <div class="p-6 pb-4">
      <div class="flex justify-center sm:justify-start mb-3">
        <div class="rounded-full bg-blue-50 p-3 text-blue-500">
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </div>
      </div>
      <h2 class="text-2xl font-semibold mb-2">New Simplified Layout for JobTread Importing</h2>
      <p class="text-gray-600">We've streamlined your workflow by combining two functions into one interface.</p>
    </div>

    <!-- Body -->
    <div class="px-6 pb-6">
      <div class="space-y-5">

        <div>
          <p class="text-sm text-gray-700">
            The following two separate menu items have been combined into a single function:
          </p>
        </div>

        <!-- Before / After Comparison -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">

          <!-- Before -->
          <div class="space-y-3">
            <div class="text-xs font-semibold text-gray-600 uppercase tracking-wide text-center">Before</div>
            <div class="space-y-3 bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl border-2 border-gray-300">
              <div class="rounded-lg overflow-hidden border border-gray-200 sidebar-screenshot">
                <img src="/images/sidebar-old-import-from-xero.png" alt="Import from Xero menu item" class="w-full h-full object-cover object-left" />
              </div>
              <div class="rounded-lg overflow-hidden border border-gray-200 sidebar-screenshot">
                <img src="/images/sidebar-old-sync-invoices.png" alt="Sync Invoices to Xero menu item" class="w-full h-full object-cover object-left" />
              </div>
              <div class="text-center text-xs text-gray-500 pt-1">2 separate functions</div>
            </div>
          </div>

          <!-- Arrow -->
          <div class="flex justify-center items-center">
            <div class="bg-blue-500 text-white rounded-full p-3">
              <svg class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>

          <!-- After -->
          <div class="space-y-3">
            <div class="text-xs font-semibold text-blue-600 uppercase tracking-wide text-center">Now</div>
            <div class="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border-2 border-blue-400">
              <div class="rounded-lg overflow-hidden border border-blue-200">
                <img src="/images/sidebar-new-jt-to-xero.png" alt="Import Bills & Invoices menu item" class="w-full object-contain" />
              </div>
              <div class="text-center text-xs text-blue-700 font-medium pt-4">1 unified function</div>
            </div>
          </div>
        </div>

        <!-- Benefits and Settings Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

          <!-- Benefits -->
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-300">
            <h4 class="font-semibold text-sm text-green-900 mb-3">Why this is better:</h4>
            <div class="space-y-2">
              <div class="flex gap-2 items-start">
                <span class="text-green-600 text-lg shrink-0">&#9889;</span>
                <span class="text-sm text-green-900">All JobTread import functions in one place</span>
              </div>
              <div class="flex gap-2 items-start">
                <span class="text-green-600 text-lg shrink-0">&#127919;</span>
                <span class="text-sm text-green-900">Prepare the way for new features that are upcoming</span>
              </div>
              <div class="flex gap-2 items-start">
                <span class="text-green-600 text-lg shrink-0">&#128230;</span>
                <span class="text-sm text-green-900">Faster workflow with less navigation</span>
              </div>
            </div>
          </div>

          <!-- Settings notice -->
          <div class="flex gap-3 items-start p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300">
            <div class="bg-amber-100 rounded-full p-2.5 shrink-0">
              <svg class="w-5 h-5 text-amber-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </div>
            <div class="space-y-1">
              <p class="font-semibold text-sm text-amber-900">Prefer the old layout?</p>
              <p class="text-sm text-amber-800">
                For a time, you can switch back to the previous layout in your <span class="font-semibold">Settings</span> page.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 pb-6 pt-4 border-t flex flex-col sm:flex-row sm:justify-end gap-2">
      <button
        on:click={dismiss}
        class="w-full sm:w-auto px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium border-0"
      >
        Got it, thanks!
      </button>
    </div>

  </div>
</div>

<style>
  @keyframes modalFadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes modalFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  .animate-fade-in {
    animation: modalFadeIn 0.2s ease-out forwards;
  }
  .fade-out {
    animation: modalFadeOut 0.2s ease-in forwards;
  }
  .sidebar-screenshot {
    height: 44px;
  }
  .sidebar-screenshot img {
    display: block;
  }
</style>
